import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiShield } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const ACTIONS = ["view", "create", "edit", "delete"];

// A few backend modules don't (yet) have a matching sidebar page — granting
// them changes what the API allows, but there's nothing new to click on.
// Flagged here so it's obvious instead of looking like a bug.
const MODULE_NOTES = {
  analytics: "Dashboard widget only — no separate page",
  "blocked-ips": "No frontend page yet",
};

const authHeaders = (token, json = true) => ({
  Authorization: `Bearer ${token}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const RoleForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [form, setForm] = useState({ name: "", description: "" });
  const [catalog, setCatalog] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const catalogRes = await fetch(`${baseUrl}/role/permissions`, { headers: authHeaders(token, false) });
        const catalogResult = await catalogRes.json();
        if (catalogResult.status) setCatalog(catalogResult.data || []);

        if (isEdit) {
          const roleRes = await fetch(`${baseUrl}/role/view/${id}`, { headers: authHeaders(token, false) });
          const roleResult = await roleRes.json();
          if (!roleResult.status) throw new Error(roleResult.message || "Role not found");
          setForm({ name: roleResult.data.name, description: roleResult.data.description || "" });
          setChecked(new Set((roleResult.data.permissions || []).map((p) => `${p.module}:${p.action}`)));
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, token]);

  const toggle = (module, action) => {
    const key = `${module}:${action}`;
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleModule = (module, actions) => {
    const keys = actions.map((a) => `${module}:${a}`);
    const allChecked = keys.every((k) => checked.has(k));
    setChecked((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allChecked ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const grouped = catalog.reduce((acc, p) => {
    (acc[p.module] = acc[p.module] || []).push(p.action);
    return acc;
  }, {});

  // Clicking an action column header toggles that action for every module
  // that has it — a fast way to grant "view everywhere" in one click.
  const toggleAction = (action) => {
    const keys = Object.keys(grouped).filter((m) => grouped[m].includes(action)).map((m) => `${m}:${action}`);
    const allChecked = keys.length > 0 && keys.every((k) => checked.has(k));
    setChecked((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allChecked ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const checkedCount = checked.size;
  const totalCount = catalog.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const permissions = Array.from(checked).map((key) => {
        const [module, action] = key.split(":");
        return { module, action };
      });
      const url = isEdit ? `${baseUrl}/role/update/${id}` : `${baseUrl}/role/create`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ ...form, permissions }),
      });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Save failed");
      toast.success(isEdit ? "Role updated successfully" : "Role created successfully");
      navigate("/roles");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex bg-gray-100 min-h-screen">
        <Sidebar /><div className="flex-1 flex flex-col min-w-0"><Navbar />
          <div className="flex justify-center items-center py-24">
            <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FiShield size={20} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{isEdit ? "Edit Role" : "Add Role"}</h2>
                <p className="text-sm text-gray-400 mt-0.5">Define what this role can view, create, edit and delete</p>
              </div>
            </div>
            {totalCount > 0 && (
              <span className="text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-full px-3.5 py-1.5">
                {checkedCount} of {totalCount} permissions granted
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Role Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
                  <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700">Permissions</h3>
                <p className="text-xs text-gray-400">Click a module or action name to toggle the whole row/column.</p>
              </div>
              <div className="border border-gray-100 rounded-xl overflow-hidden overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wide bg-gray-50">
                      <th className="py-3 pl-5 pr-4 font-semibold">Module</th>
                      {ACTIONS.map((a) => (
                        <th key={a} className="py-3 px-4 text-center">
                          <button type="button" onClick={() => toggleAction(a)}
                            className="capitalize font-semibold text-gray-500 hover:text-blue-600 transition">
                            {a}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.keys(grouped).map((module) => (
                      <tr key={module} className="odd:bg-white even:bg-gray-50/40 hover:bg-blue-50/40 transition">
                        <td className="py-3 pl-5 pr-4">
                          <button type="button" onClick={() => toggleModule(module, grouped[module])}
                            className="font-medium text-gray-700 capitalize hover:text-blue-600 text-left transition">
                            {module.replace(/-/g, " ")}
                          </button>
                          {MODULE_NOTES[module] && (
                            <span className="block text-[11px] text-amber-600/80 normal-case mt-0.5">{MODULE_NOTES[module]}</span>
                          )}
                        </td>
                        {ACTIONS.map((action) => {
                          const has = grouped[module].includes(action);
                          const key = `${module}:${action}`;
                          return (
                            <td key={action} className="py-3 px-4 text-center">
                              {has ? (
                                <input type="checkbox" checked={checked.has(key)} onChange={() => toggle(module, action)}
                                  className="w-4.5 h-4.5 rounded accent-blue-600 cursor-pointer" style={{ width: 18, height: 18 }} />
                              ) : (
                                <span className="text-gray-200">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={submitting}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
                <FiSave size={14} /> {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Role"}
              </button>
              <button type="button" onClick={() => navigate("/roles")} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;
